import { useState, useEffect, useMemo } from 'react';
import { useContractRead, useContractWrite, usePrepareContractWrite } from 'wagmi';
import { Abi } from 'viem';
import { toast } from 'react-toastify';

interface Term {
  returnPercentage: string;
  duration: string;
  active: boolean;
}

interface UseTermsManagementProps {
  productAddress?: `0x${string}`;
  productAbi: Abi;
}

export function useTermsManagement({ productAddress, productAbi }: UseTermsManagementProps) {
  const [terms, setTerms] = useState<Term[]>([]);
  const [originalTerms, setOriginalTerms] = useState<Term[]>([]);
  const [isEditingTerms, setIsEditingTerms] = useState(false);
  const [newTermReturn, setNewTermReturn] = useState('');
  const [newTermDuration, setNewTermDuration] = useState('');

  // Compute if terms have changed
  const hasTermsChanged = useMemo(() => {
    if (terms.length !== originalTerms.length) return true;
    return terms.some((term, index) => {
      const originalTerm = originalTerms[index];
      return term.returnPercentage !== originalTerm.returnPercentage ||
             term.duration !== originalTerm.duration ||
             term.active !== originalTerm.active;
    });
  }, [terms, originalTerms]);

  // Contract reads
  const { data: termsData, isLoading: isLoadingTerms, refetch: refetchTerms } = useContractRead({
    address: productAddress,
    abi: productAbi,
    functionName: 'getTerms',
    enabled: !!productAddress,
  });

  // Update terms when contract data changes
  useEffect(() => {
    if (termsData && !isLoadingTerms) {
      try {
        const formattedTerms = (termsData as any[]).map((term: any) => ({
          returnPercentage: (Number(term.returnPercentage) / 100).toString(),
          duration: (Number(term.duration) / 86400).toString(), // Convert from seconds to days
          active: term.active
        }));
        setTerms(formattedTerms);
        setOriginalTerms(formattedTerms);
      } catch (error) {
        console.error('Error formatting terms:', error);
        toast.error('Error loading terms data');
      }
    }
  }, [termsData, isLoadingTerms]);

  // Contract writes
  const { config: updateTermsConfig } = usePrepareContractWrite({
    address: productAddress,
    abi: productAbi,
    functionName: 'updateAllTerms',
    enabled: !!productAddress && hasTermsChanged && !!productAbi,
    args: [terms.map(term => ({
      returnPercentage: BigInt(Math.round(Number(term.returnPercentage) * 100)),
      duration: BigInt(Math.round(Number(term.duration) * 86400)), // Convert days to seconds
      active: term.active
    }))]
  });

  const { 
    writeAsync: updateTerms,
    isLoading: isUpdatingTerms,
    error: updateError
  } = useContractWrite(updateTermsConfig || {
    address: productAddress,
    abi: productAbi,
    functionName: 'updateAllTerms',
    args: [[]]
  });

  // Terms management functions
  const addTerm = () => {
    if (newTermReturn && newTermDuration) {
      setTerms(prev => [...prev, {
        returnPercentage: newTermReturn,
        duration: newTermDuration,
        active: true
      }]);
      setNewTermReturn('');
      setNewTermDuration('');
    }
  };

  const removeTerm = (index: number) => {
    setTerms(prev => prev.filter((_, i) => i !== index));
  };

  const updateTerm = (index: number, field: keyof Term, value: string | boolean) => {
    setTerms(prev => prev.map((term, i) => 
      i === index ? { ...term, [field]: value } : term
    ));
  };

  const saveTerms = async () => {
    if (!updateTerms) {
      toast.error('Unable to update terms at this time');
      return;
    }

    try {
      await updateTerms();
      await refetchTerms();
      setOriginalTerms(terms);
      toast.success('Terms updated successfully');
    } catch (error) {
      console.error('Error updating terms:', error);
      toast.error('Failed to update terms');
    }
  };

  const cancelEdit = () => {
    setTerms(originalTerms);
    setIsEditingTerms(false);
    setNewTermReturn('');
    setNewTermDuration('');
  };

  return {
    terms,
    isEditingTerms,
    isLoadingTerms,
    isUpdatingTerms,
    updateError,
    hasTermsChanged,
    newTermReturn,
    newTermDuration,
    setNewTermReturn,
    setNewTermDuration,
    setIsEditingTerms,
    setTerms,
    setOriginalTerms,
    addTerm,
    removeTerm,
    updateTerm,
    saveTerms,
    cancelEdit,
    refetchTerms
  };
} 